import { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import ConditionalRender from "../../../../../ConditionalRender";
import { SelectionControl } from "../../../../../design_system/components/SelectionControls/SelectionControl.jsx";
import { Loader } from "../../../../../design_system/components/Loader.jsx";
import { Tooltip } from "../../../../../design_system/components/ToolTip/ToolTip.jsx";
import { indexOfObject } from "../../../../utils/ArrayUtils";
import humanizeString from "../../../../utils/stringUtils";
import AggregatesListContext from "./aggregates_list_context";
import SearchLensSad from "../../../../../assets/SearchLensSad.svg";

import { fetchAggregates } from "../../../aggregates/apiHitter";
import { setState } from "../../../../utils/CommonReducer";
import { get_data_params } from "../../../../views/news_feed/utils/newsFeedUtils";

const DEFAULT_AGG_SIZE = 20
const MAX_AGG_SIZE = 1000

const TruncatedLabel = ({ text }) => {
    return (
        <Tooltip tooltipText={text} placement="bottom" wrapperStyle={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {text}
        </Tooltip>
    );
};

export const AggregatesList = ({showsearchedRes, ...props}) => {
    const { currfilterState, setCurrFilterState, filterTab, isLoading, allFilterTabs, tagsList, type} = useContext(AggregatesListContext);
    const currentUser = useSelector((state) => state.current_user.user);
    const {loadMore, data_params, filtersSelected} = currfilterState
    const mappedKey = filterTab.tab_key
    const isAssignsTab = mappedKey === "assigns";
    const sortedTagsList = useMemo(() => {
        if (!tagsList || tagsList.length === 0) return tagsList;
        if (!isAssignsTab) return tagsList.map((item) => {
            const displayTerm = filterTab.is_custom && item.label ? item.label : humanizeString(item.term);
            return { ...item, fullLabel: `${displayTerm}${item.count ? ` (${item.count})` : ''}` };
        });

        const list = tagsList.map((item) => {
            const displayTerm = item.term === currentUser?.id ? "Me" : humanizeString(item.label || item.term);
            return { ...item, fullLabel: `${displayTerm}${item.count ? ` (${item.count})` : ''}` };
        });

        const meIndex = list.findIndex((item) => item.term === currentUser?.id);
        if (meIndex > 1) {
            const [meItem] = list.splice(meIndex, 1);
            list.splice(1, 0, meItem);
        }
        return list;
    }, [tagsList, mappedKey, currentUser?.id, filterTab.is_custom]);
    const filterType = 'include';

    const fetch_aggregates = (agg_size = null) => {
        let loader = loadMore ? "loadMoreLoader" : "tagsListLoader"
        setState(setCurrFilterState)({isLoading: {[loader]: true}})
        let final_data_params = {...data_params, ...get_data_params(type, {filtersState: currfilterState, allFilterTabs: allFilterTabs})}
        final_data_params.term = filterTab.is_custom ? 'custom_option_ids' : mappedKey
        if(agg_size)
            final_data_params.agg_size = agg_size
        fetchAggregates(final_data_params, type).then((result) => {
            setCurrFilterState({type: "ASSIGN_AGGREGATES_DATA", payload: {aggregates: result.data.aggregates, filterType: filterType, mappedKey: mappedKey}})
            setState(setCurrFilterState)({isLoading: {[loader]: false}})
        }).catch((err)=> {
            setState(setCurrFilterState)({isLoading: {[loader]: false}})
        })
    }

    useEffect(() => {
        fetch_aggregates();
        setState(setCurrFilterState)({loadMore: false})
    }, [mappedKey])

    useEffect(() => {
        if(loadMore)
            fetch_aggregates(1000);
    }, [loadMore])

    const handleTagsChange = (e, item) => {
        if(type==="smartpublish"){
            props.handleFilterCheckBoxChange(e)
        } else {
            let val = e.target.value
            setCurrFilterState({
                type: "SELECTED_FILTER",
                payload: {
                    mappedKey: mappedKey,
                    filterType: filterType,
                    selectedVal: val,
                    selectedAgg: item
                },
            });
        }
    }

    return (
        <div
            className={`aggregates-list__body flex-1 scroll-y justify-center mt-l${sortedTagsList.length === 0 ? " default_flex_container" : ""}`}
        >
            <ConditionalRender if={isLoading.tagsListLoader}>
                <div className="w-full h-full flex items-center justify-center">
                    <Loader isLoading={true} Size="Medium" isFullPage={false}/>
                </div>
            </ConditionalRender>

            <ConditionalRender if={!isLoading.tagsListLoader}>
                {sortedTagsList.length > 0 ? (
                    <div className="aggregates-list__tag-list">
                        {sortedTagsList.map((item, index) => {
                            if (!item.term) return null;
                            return (
                                <div key={item.term + index} className="filter-popup__item aggregates-list__tag-item mb-s">
                                    <SelectionControl
                                        type="checkbox"
                                        label={<TruncatedLabel text={item.fullLabel} />}
                                        checked={item.is_checked}
                                        value={item.term}
                                        disabled={item.applied || item.disabled}
                                        onChange={(e) => handleTagsChange(e, item)}
                                    />
                                </div>
                            )
                        })}
                        {isLoading.loadMoreLoader ? (
                            <div className="flex justify-center w-full mt-m">
                                <Loader isLoading={true} Size="Small" />
                            </div>
                        ) : sortedTagsList.length === MAX_AGG_SIZE ? (
                            <span className="body-s color-text-secondary">
                                This is a partial list. Use search to refine further.
                            </span>
                        ) : (
                            <ConditionalRender if={!loadMore && sortedTagsList.length >= DEFAULT_AGG_SIZE}>
                                <span
                                    className="body-s color-text-secondary cursor-pointer"
                                    onClick={() => setState(setCurrFilterState)({loadMore: true})}
                                >
                                    <span className="aggregates-list__load-more-text link-subtle">Load More</span> ...
                                </span>
                            </ConditionalRender>
                        )}
                    </div>
                ) : (
                    <div className="aggregates-list__empty text-center">
                        <img src={SearchLensSad} className="aggregates-list__empty-img" alt="" />
                        <div className="body-m-semibold color-text-secondary mt-m">
                            {showsearchedRes ? "No results match your search criteria" : "No results available"}
                        </div>
                    </div>
                )}
            </ConditionalRender>
        </div>
    )
}
